import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { z } from 'zod';

export type RealtimeSocketConfig<T> = {
  url: string;
  eventType: string;
  payloadKey: string;
  schema: z.ZodType<T>;
};

export type RealtimeSocketEvent<T> = {
  eventType: string;
  payload: T;
};

@Injectable({ providedIn: 'root' })
export class RealtimeSocketService {
  listen<T>(config: RealtimeSocketConfig<T>): Observable<T> {
    return new Observable<T>((subscriber) => {
      const socket = new WebSocket(config.url);

      socket.onmessage = (messageEvent: MessageEvent) => {
        const message = this.parseMessage(messageEvent);
        if (!message || message['type'] !== config.eventType) {
          return;
        }
        const parsed = config.schema.safeParse(message[config.payloadKey]);
        if (parsed.success) {
          subscriber.next(parsed.data);
        }
      };

      return () => {
        socket.onmessage = null;
        socket.close();
      };
    });
  }

  listenMany<T>(
    url: string,
    eventConfigs: ReadonlyArray<Omit<RealtimeSocketConfig<T>, 'url'>>,
  ): Observable<RealtimeSocketEvent<T>> {
    return new Observable<RealtimeSocketEvent<T>>((subscriber) => {
      const socket = new WebSocket(url);

      socket.onmessage = (messageEvent: MessageEvent) => {
        const message = this.parseMessage(messageEvent);
        if (!message) {
          return;
        }
        const config = eventConfigs.find(
          (candidate) => candidate.eventType === message['type'],
        );
        if (!config) {
          return;
        }
        const parsed = config.schema.safeParse(message[config.payloadKey]);
        if (parsed.success) {
          subscriber.next({
            eventType: config.eventType,
            payload: parsed.data,
          });
        }
      };

      return () => {
        socket.onmessage = null;
        socket.close();
      };
    });
  }

  private parseMessage(
    messageEvent: MessageEvent,
  ): Record<string, unknown> | null {
    try {
      const parsed: unknown = JSON.parse(messageEvent.data as string);
      return typeof parsed === 'object' && parsed !== null
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  // TODO: Implement reconnect, onclose, onerror, etc.
}
